import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { MapView, mapPhase } from "@/components/MapView";
import { MediaLightbox } from "@/components/MediaLightbox";
import { api, thumbUrl, posterUrl, resolveAvatar } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { MapPin, Search, X, Loader2, Flame, Star, ImageIcon, Video, ChevronRight, ChevronLeft, Layers, LassoSelect } from "lucide-react";
import { toast } from "sonner";

const TYPE_API = { all: undefined, photos: "image", videos: "video" };

// Safe card imagery: images via resized thumb; videos ONLY via derived poster (never the raw .mov)
const cardSrc = (m, w = 400) => (m.media_type === "video" ? (m.cloud_poster_url || undefined) : (m.file_ref ? thumbUrl(m.file_ref, w) : posterUrl(m, w)));

const CardImg = ({ m, w = 400, className }) => (
  cardSrc(m, w)
    ? <img src={cardSrc(m, w)} alt={m.title || ""} className={className} />
    : <div className={cn(className, "flex items-center justify-center bg-secondary/40 text-muted-foreground")}><Video className="w-5 h-5" /></div>
);

const ExploreMap = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const reqSeq = useRef(0); // newest drawer request wins; stale responses are dropped
  const [type, setType] = useState("all");
  const [source, setSource] = useState("all");
  const [sort, setSort] = useState("latest");
  const [radius, setRadius] = useState(250);
  const [points, setPoints] = useState([]);
  const [view, setView] = useState({ center: { lat: 20, lng: 10 }, zoom: 2 });
  const [loadingMap, setLoadingMap] = useState(true);
  const [drawMode, setDrawMode] = useState(false);
  const [areaSel, setAreaSel] = useState(null); // {lat,lng,radiusKm}
  const [areaResults, setAreaResults] = useState([]);
  const [areaLoading, setAreaLoading] = useState(false);

  const [locSearch, setLocSearch] = useState("");
  const [locResults, setLocResults] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null); // {slug, city, ...}
  const [panel, setPanel] = useState(null); // /location payload
  const [panelTab, setPanelTab] = useState("uploads");
  const [panelLoading, setPanelLoading] = useState(false);
  const [panelError, setPanelError] = useState(null);
  const [nearby, setNearby] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [live, setLive] = useState(false);
  const [pulses, setPulses] = useState([]);
  const activityRef = useRef({ queue: [], idx: 0 });
  const carouselRef = useRef(null);

  useEffect(() => {
    if (!live) { setPulses([]); return; }
    let alive = true;
    api.get("/map/activity", { params: { limit: 40 } }).then((r) => { activityRef.current = { queue: r.data, idx: 0 }; }).catch(() => {});
    const tick = () => {
      const st = activityRef.current;
      if (!st.queue.length) return;
      const a = st.queue[st.idx % st.queue.length];
      st.idx += 1;
      const key = `${a.id}-${Date.now()}`;
      if (!alive) return;
      setPulses((p) => [...p, { ...a, key }]);
      toast(`New upload — ${a.city}`, { description: a.title, action: { label: "Open", onClick: () => setSelectedMedia(a.id) } });
      setTimeout(() => { if (alive) setPulses((p) => p.filter((x) => x.key !== key)); }, 6000);
    };
    const first = setTimeout(tick, 800);
    const id = setInterval(tick, 3500);
    return () => { alive = false; clearInterval(id); clearTimeout(first); };
  }, [live]);

  const commonParams = useCallback(() => {
    const p = {};
    if (TYPE_API[type]) p.type = TYPE_API[type];
    if (source !== "all") p.source = source;
    return p;
  }, [type, source]);

  // ONE dataset for every zoom: per-media eligible points → heat, geographic clusters and final location markers (client-side)
  const loadMap = useCallback(async () => {
    setLoadingMap(true);
    try {
      const r = await api.get("/map/points", { params: commonParams() });
      setPoints(r.data);
    } finally { setLoadingMap(false); }
  }, [commonParams]);

  useEffect(() => { loadMap(); }, [loadMap]);

  const loadNearby = useCallback(async (lat, lng) => {
    try {
      const r = await api.get("/map/nearby", { params: { lat, lng, radius, limit: 24, ...commonParams() } });
      setNearby(r.data);
    } catch { setNearby([]); }
  }, [radius, commonParams]);

  useEffect(() => {
    // populate the carousel on first load regardless of map center
    api.get("/map/nearby", { params: { lat: 20, lng: 10, radius: 20000, limit: 24 } }).then((r) => setNearby(r.data)).catch(() => {});
  }, []); // initial

  const openCity = useCallback(async (city) => {
    const seq = ++reqSeq.current;
    setAreaSel(null);
    setSelectedCity(city);
    setPanelTab("uploads");
    setPanel(null);          // never show the previous location's cards/title/counts while loading
    setPanelError(null);
    setPanelLoading(true);
    try {
      const r = await api.get(`/location/${city.slug}`, { params: { sort: sort === "latest" ? "recent" : sort, ...commonParams() } });
      if (seq !== reqSeq.current) return; // an older response must never overwrite a newer selection
      setPanel(r.data);
      const c = r.data.group_center || { lat: r.data.lat, lng: r.data.lng };   // Iter125w: ONE group centre (mean of members) — never one upload's point
      setMapCenter([c.lat, c.lng]);
      loadNearby(c.lat, c.lng);
    } catch (e) {
      if (seq !== reqSeq.current) return;
      setPanel(null);
      setPanelError(e?.response?.status === 404 ? "No uploads here match the current filters." : "Couldn't load this location. Try again.");
    } finally { if (seq === reqSeq.current) setPanelLoading(false); }
  }, [sort, commonParams, loadNearby]);

  // reload panel when sort/type changes while open
  useEffect(() => { if (selectedCity) openCity(selectedCity); }, [sort, type, source]); // eslint-disable-line

  // Clicking a final location marker opens the location panel (geographic clusters fit their member bounds inside MapView).
  const onClusterClick = useCallback((c) => { if (c.slug) openCity(c); }, [openCity]);

  const runSearch = async (q) => {
    setLocSearch(q);
    if (q.trim().length < 2) { setLocResults([]); return; }
    try { const r = await api.get("/locations/search", { params: { q } }); setLocResults(r.data); } catch {}
  };

  const gotoCityFromSearch = (c) => {
    setLocResults([]); setLocSearch(`${c.city}, ${c.country}`);
    setMapCenter([c.lat, c.lng]);
    const slug = `${c.city.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}-${c.cc.toLowerCase()}`;
    openCity({ slug, city: c.city, country: c.country });
  };

  const scrollCarousel = (dir) => { carouselRef.current?.scrollBy({ left: dir * 400, behavior: "smooth" }); };

  const onDrawArea = useCallback(async (center, radiusKm) => {
    setAreaSel({ ...center, radiusKm });
    setSelectedCity(null); setPanel(null);
    setAreaLoading(true);
    try {
      const r = await api.get("/map/nearby", { params: { lat: center.lat, lng: center.lng, radius: radiusKm, limit: 60, ...commonParams() } });
      setAreaResults(r.data);
    } catch { setAreaResults([]); }
    setAreaLoading(false);
  }, [commonParams]);

  const Select = ({ label, value, onChange, options, testid }) => (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground mb-1">{label}</span>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="bg-card border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-primary min-w-[130px]">
        {options.map((o) => <option key={o[0]} value={o[0]}>{o[1]}</option>)}
      </select>
    </div>
  );

  return (
    <div className="min-h-screen relative z-10">
      <Navbar />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-8 py-6">
        <div className="flex items-center gap-2 text-primary mb-1"><MapPin className="w-4 h-4" /><span className="text-xs tracking-[0.2em] uppercase font-bold">Explore by Location</span></div>
        <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tighter">Explore by Location</h1>
        <p className="text-muted-foreground mt-2">Discover uploads and creators from around the world.</p>

        {/* filters */}
        <div className="flex flex-wrap items-end gap-3 mt-6">
          <Select testid="map-type" label="Content Type" value={type} onChange={setType} options={[["all", "All"], ["photos", "Photos"], ["videos", "Videos"]]} />
          <Select testid="map-source" label="Source" value={source} onChange={setSource} options={[["all", "All"], ["barefootage", "BareFootage"], ["community", "Community"]]} />
          <div className="flex flex-col relative">
            <span className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground mb-1">Location</span>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input data-testid="map-location-search" value={locSearch} onChange={(e) => runSearch(e.target.value)} placeholder="Search location…"
                className="bg-card border border-border rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:border-primary w-56" />
            </div>
            {locResults.length > 0 && (
              <div className="absolute top-full mt-1 z-[500] w-56 bg-popover border border-border rounded-lg overflow-hidden shadow-xl">
                {locResults.map((c) => (
                  <button key={`${c.city}-${c.cc}`} data-testid={`map-locresult-${c.cc}`} onClick={() => gotoCityFromSearch(c)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-secondary/60 flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-primary" /> {c.city}, <span className="text-muted-foreground">{c.country}</span></button>
                ))}
              </div>
            )}
          </div>
          <Select testid="map-sort" label="Sort By" value={sort} onChange={setSort} options={[["latest", "Latest"], ["trending", "Trending"], ["top", "Top Rated"], ["most", "Most Rated"]]} />
          <Select testid="map-radius" label="Radius" value={radius} onChange={(v) => setRadius(Number(v))} options={[[10, "10 km"], [25, "25 km"], [50, "50 km"], [100, "100 km"], [250, "250 km"], [10000, "Any distance"]]} />
          <div className="ml-auto flex items-center gap-2">
            <button data-testid="map-draw-toggle" onClick={() => setDrawMode((v) => !v)}
              className={cn("hidden md:flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border transition-colors", drawMode ? "bg-primary text-primary-foreground border-primary font-medium" : "bg-card border-border text-muted-foreground hover:text-foreground")}>
              <LassoSelect className="w-4 h-4" /> Draw area
            </button>
            <button data-testid="map-live-toggle" onClick={() => setLive((v) => !v)}
              className={cn("flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border transition-colors", live ? "bg-primary text-primary-foreground border-primary font-medium" : "bg-card border-border text-muted-foreground hover:text-foreground")}>
              <span className={cn("w-2 h-2 rounded-full", live ? "bg-primary-foreground animate-pulse" : "bg-muted-foreground")} /> Live
            </button>
          </div>
        </div>

        {/* map + panel */}
        <div className={cn("grid gap-4 mt-4", panel || selectedCity || areaSel ? "lg:grid-cols-[1fr_380px]" : "grid-cols-1")}>
          <div className="relative rounded-xl overflow-hidden border border-border h-[560px]">
            <MapView points={points} pulses={pulses} center={[20, 10]} zoom={2} flyTo={mapCenter} flyZoom={8}
              drawMode={drawMode} onDrawArea={onDrawArea}
              onSelectCity={onClusterClick} onSelectMedia={(m) => setSelectedMedia(m.id)}
              onMove={setView} />
            {/* auto phase indicator — same dataset at every zoom, only the visualisation changes */}
            <div data-testid="map-mode-indicator" data-phase={mapPhase(view.zoom)} className="absolute bottom-3 right-3 z-[500] bg-black/70 backdrop-blur border border-border rounded-full px-3 py-1.5 text-xs flex items-center gap-1.5">
              {mapPhase(view.zoom) === "heat" ? <><Flame className="w-3.5 h-3.5 text-primary" /> Heat map</>
                : mapPhase(view.zoom) === "heat+clusters" ? <><Layers className="w-3.5 h-3.5 text-primary" /> Heat + clusters</>
                : <><Layers className="w-3.5 h-3.5 text-primary" /> Locations</>}
              <span className="text-muted-foreground hidden sm:inline">· {mapPhase(view.zoom) === "clusters" ? "click a cluster to zoom in · click a location for all its uploads" : "zoom in for detail"}</span>
            </div>
            {drawMode && (
              <div data-testid="map-draw-hint" className="absolute top-3 left-1/2 -translate-x-1/2 z-[500] bg-primary text-primary-foreground rounded-full px-4 py-1.5 text-xs font-medium shadow-lg pointer-events-none">
                Click &amp; drag on the map to draw a circle — release to see every pic inside
              </div>
            )}
            {loadingMap && <div className="absolute top-3 right-3 z-[500] bg-black/60 rounded-full p-2"><Loader2 className="w-4 h-4 animate-spin text-primary" /></div>}
            {/* density legend — only meaningful for heat/clusters */}
            <div className="absolute bottom-3 left-3 z-[500] bg-black/70 backdrop-blur border border-border rounded-lg p-3 w-44">
              <div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground mb-1.5">Uploads Density</div>
              <div className="h-2 rounded-full" style={{ background: "linear-gradient(90deg,#3a2a00,#d9a24a,#ffe9a8)" }} />
              <div className="flex justify-between text-[10px] text-muted-foreground mt-1"><span>Low</span><span>High</span></div>
            </div>
          </div>

          {areaSel ? (
            <div data-testid="area-panel" className="rounded-xl border border-border bg-card overflow-hidden flex flex-col h-[560px]">
              <div className="p-4 border-b border-border flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-1.5 text-primary"><LassoSelect className="w-4 h-4" /><h3 className="font-heading text-xl">Selected area</h3></div>
                  <div className="text-xs text-muted-foreground">~{areaSel.radiusKm} km radius · {areaResults.length} uploads</div>
                </div>
                <button data-testid="area-panel-close" onClick={() => { setAreaSel(null); setAreaResults([]); }} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-3">
                {areaLoading ? <div className="h-full flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                  : areaResults.length === 0 ? <div className="h-full flex items-center justify-center text-sm text-muted-foreground text-center px-4">No pics inside that circle. Draw a bigger one or move to a busier spot.</div>
                  : <div className="grid grid-cols-3 gap-2">
                      {areaResults.map((m) => (
                        <button key={m.id} data-testid={`area-upload-${m.id}`} onClick={() => setSelectedMedia(m.id)} className="group relative aspect-square rounded-lg overflow-hidden bg-black">
                          <CardImg m={m} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                          <span className="absolute top-1 left-1 text-[8px] uppercase font-bold px-1 py-0.5 rounded bg-black/70 flex items-center gap-0.5">{m.media_type === "video" ? <Video className="w-2.5 h-2.5" /> : <ImageIcon className="w-2.5 h-2.5" />}</span>
                          <span className="absolute bottom-1 right-1 text-[10px] font-bold px-1 py-0.5 rounded bg-black/70 flex items-center gap-0.5"><Star className="w-2.5 h-2.5 text-primary fill-primary" />{m.rating_count ? m.avg_rating : "—"}</span>
                        </button>
                      ))}
                    </div>}
              </div>
            </div>
          ) : (panel || selectedCity) && (
            <div data-testid="location-panel" className="rounded-xl border border-border bg-card overflow-hidden flex flex-col h-[560px]">
              <div className="p-4 border-b border-border flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-1.5 text-primary"><MapPin className="w-4 h-4" /><h3 data-testid="location-panel-title" className="font-heading text-xl">{selectedCity?.city}</h3></div>
                  <div className="text-xs text-muted-foreground">{panel?.country || selectedCity?.country}</div>
                  {panel && !panelLoading && (
                    <div data-testid="location-panel-total" data-total={panel.total_unique_media} className="text-xs text-muted-foreground mt-0.5">
                      {panel.total_unique_media} photos &amp; videos
                      <span className="opacity-70"> · {panel.photo_count} {panel.photo_count === 1 ? "photo" : "photos"} · {panel.video_count} {panel.video_count === 1 ? "video" : "videos"} · {panel.uploader_count} {panel.uploader_count === 1 ? "uploader" : "uploaders"}</span>
                    </div>
                  )}
                </div>
                <button data-testid="location-panel-close" onClick={() => { setSelectedCity(null); setPanel(null); }} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
              </div>
              <div className="flex border-b border-border">
                {[["uploads", "Uploads"], ["uploaders", "Uploaders"]].map(([k, l]) => (
                  <button key={k} data-testid={`location-tab-${k}`} onClick={() => setPanelTab(k)}
                    className={cn("flex-1 py-2.5 text-sm font-medium transition-colors", panelTab === k ? "text-primary border-b-2 border-primary" : "text-muted-foreground hover:text-foreground")}>{l}</button>
                ))}
              </div>
              <div className="flex-1 overflow-y-auto p-3">
                {panelLoading ? <div data-testid="location-panel-loading" className="h-full flex flex-col items-center justify-center gap-2 text-xs text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin text-primary" />Loading {selectedCity?.city}…</div>
                  : !panel ? <div data-testid="location-panel-empty" className="h-full flex items-center justify-center text-sm text-muted-foreground text-center px-4">{panelError || "No uploads here yet. Move the map or pick a busier city."}</div>
                  : panelTab === "uploads" ? (
                    <div className="grid grid-cols-3 gap-2" data-testid="location-panel-uploads" data-count={panel.uploads.length}>
                      {panel.uploads.length === 0 && <div className="col-span-3 py-10 text-center text-sm text-muted-foreground">Nothing here matches the current filters.</div>}
                      {panel.uploads.map((m) => (
                        <button key={m.id} data-testid={`panel-upload-${m.id}`} onClick={() => setSelectedMedia(m.id)} className="group relative aspect-square rounded-lg overflow-hidden bg-black">
                          <CardImg m={m} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                          <span className="absolute top-1 left-1 text-[8px] uppercase font-bold px-1 py-0.5 rounded bg-black/70 flex items-center gap-0.5">{m.media_type === "video" ? <Video className="w-2.5 h-2.5" /> : <ImageIcon className="w-2.5 h-2.5" />}</span>
                          <span className="absolute bottom-1 right-1 text-[10px] font-bold px-1 py-0.5 rounded bg-black/70 flex items-center gap-0.5"><Star className="w-2.5 h-2.5 text-primary fill-primary" />{m.rating_count ? m.avg_rating : "—"}</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {panel.uploaders.map((u) => (
                        <div key={u.user_id} data-testid={`panel-uploader-${u.user_id}`} className="rounded-lg border border-border p-3">
                          <div className="flex items-center gap-3">
                            <Avatar className="w-10 h-10 border border-border"><AvatarImage src={resolveAvatar(u.avatar_url)} /><AvatarFallback className="bg-secondary text-xs">{u.name?.[0]}</AvatarFallback></Avatar>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium truncate">@{u.name}</div>
                              <div className="text-xs text-muted-foreground">{u.count} uploads · ★ {u.avg_rating || "—"}</div>
                            </div>
                          </div>
                          <div className="flex gap-1 mt-2">
                            {u.thumbs.map((t) => <div key={t.id} className="w-1/3 aspect-square rounded overflow-hidden bg-black"><CardImg m={t} w={200} className="w-full h-full object-cover" /></div>)}
                          </div>
                          <Button data-testid={`panel-uploader-view-${u.user_id}`} onClick={() => navigate(`/u/${u.user_id}`)} variant="outline" size="sm" className="w-full mt-2 rounded-full border-border">View Profile</Button>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
              {panel && !panelLoading && (
                <div className="p-3 border-t border-border space-y-2">
                  {panel.has_more && <div data-testid="location-panel-capped" className="text-[11px] text-muted-foreground text-center">Showing first {panel.uploads.length} of {panel.total_unique_media}</div>}
                  <Button data-testid="location-view-gallery" onClick={() => navigate(`/place/${panel.slug}`)} className="w-full rounded-full">View all {panel.total_unique_media} <ChevronRight className="w-4 h-4 ml-1" /></Button>
                  {user?.is_admin && panel.diagnostics && (
                    <details data-testid="map-diagnostics" className="text-[11px] text-muted-foreground rounded-lg border border-border/60 bg-black/30 p-2">
                      <summary className={cn("cursor-pointer select-none", panel.diagnostics.mismatch ? "text-red-400 font-medium" : "")}>
                        Admin diagnostics {panel.diagnostics.mismatch ? "— MISMATCH map ≠ drawer" : "— reconciled"}
                      </summary>
                      <div className="mt-1.5 space-y-0.5 font-mono">
                        <div>location_key: <span data-testid="diag-location-key">{panel.diagnostics.location_key}</span></div>
                        <div>map total: <span data-testid="diag-map-total">{panel.diagnostics.map_total}</span> · drawer total: <span data-testid="diag-drawer-total">{panel.diagnostics.drawer_total}</span> · unique IDs: <span data-testid="diag-unique">{panel.diagnostics.unique_media_count}</span></div>
                        <div>duplicate IDs: <span data-testid="diag-dup-ids">{panel.diagnostics.duplicate_ids.length ? panel.diagnostics.duplicate_ids.join(", ") : "none"}</span></div>
                        <div>visually identical, different IDs: <span data-testid="diag-visual-dups">{panel.diagnostics.visual_duplicate_groups.length ? panel.diagnostics.visual_duplicate_groups.map((g) => g.map((i) => i.slice(0, 8)).join("=")).join(" | ") : "none"}</span> (reported only — nothing merged)</div>
                        <div>filters: type={panel.diagnostics.filters_active.type} · source={panel.diagnostics.filters_active.source}</div>
                        <div className="opacity-70">{panel.diagnostics.query_source}</div>
                      </div>
                    </details>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* nearby carousel */}
        {nearby.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" /><h2 className="font-heading text-xl">Nearby Uploads</h2></div>
              <div className="flex gap-2">
                <button onClick={() => scrollCarousel(-1)} className="h-8 w-8 rounded-full border border-border flex items-center justify-center hover:border-primary"><ChevronLeft className="w-4 h-4" /></button>
                <button onClick={() => scrollCarousel(1)} className="h-8 w-8 rounded-full border border-border flex items-center justify-center hover:border-primary"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
            <div ref={carouselRef} className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide" style={{ scrollbarWidth: "none" }}>
              {nearby.map((m) => (
                <button key={m.id} data-testid={`nearby-${m.id}`} onClick={() => setSelectedMedia(m.id)} className="shrink-0 w-40 text-left group">
                  <div className="relative aspect-video rounded-lg overflow-hidden bg-black">
                    <CardImg m={m} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    <span className="absolute top-1 left-1 text-[8px] uppercase font-bold px-1 py-0.5 rounded bg-black/70">{m.media_type === "video" ? "Video" : "Photo"}</span>
                    <span className="absolute bottom-1 right-1 text-[10px] font-bold px-1 py-0.5 rounded bg-black/70 flex items-center gap-0.5"><Star className="w-2.5 h-2.5 text-primary fill-primary" />{m.rating_count ? m.avg_rating : "—"}</span>
                  </div>
                  <div className="text-xs font-medium truncate mt-1">{m.title}</div>
                  <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1"><MapPin className="w-3 h-3" />{m.location?.display}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <MediaLightbox mediaId={selectedMedia} open={!!selectedMedia} onClose={() => setSelectedMedia(null)} />
    </div>
  );
};

export default ExploreMap;
